import type { AssessmentChartHighlightZone } from '~/components/maud/assessment-card';
import { DateTime } from 'luxon';

import { PatientUniversalStatusCode } from '~/lib/constants/patients.constants';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';

export type AssessmentTimepointContext = {
  chartDomain?: [number, number];
  currentEpisode?: TreatmentEpisode | null;
  tentativeDischargeDate?: string | null;
};

export type AssessmentTimelineMarker = {
  label: string;
  subLabel?: string;
  epoch: number;
  variant?: 'admit' | 'week16' | 'discharge';
};

const ACTIVE_EPISODE_STATUS_CODES = new Set<PatientUniversalStatusCode>([
  PatientUniversalStatusCode.IN_TREATMENT,
  PatientUniversalStatusCode.PAUSED,
]);

const ACTIVE_ASSESSMENT_STATUSES = new Set<SurveyResponseStatus>([
  SurveyResponseStatus.NEW,
  SurveyResponseStatus.REQUESTED,
  SurveyResponseStatus.IN_PROGRESS,
]);

const ASSESSMENT_RESPONSE_WINDOW_DAYS = 30;
const OVERDUE_ASSESSMENT_DAYS = 10;

const parseISODate = (value?: string | null): DateTime | null => {
  if (!value) return null;

  const date = DateTime.fromISO(value);
  return date.isValid ? date : null;
};

const formatHighlightDateRange = (start: DateTime, end: DateTime): string => {
  const startDate = start.startOf('day');
  const endDate = end.startOf('day');

  if (startDate.year !== endDate.year) {
    return `${startDate.toFormat('LLL d, yyyy')} - ${endDate.toFormat(
      'LLL d, yyyy',
    )}`;
  }

  return `${startDate.toFormat('LLL d')} - ${endDate.toFormat('LLL d')}`;
};

const dateValue = (date?: string | null): number => {
  return parseISODate(date)?.toMillis() ?? 0;
};

const getEpisodeStart = (episode: TreatmentEpisode): DateTime | null =>
  parseISODate(episode.createdAt) ?? parseISODate(episode.admitDate);

const getEpisodeEnd = (episode: TreatmentEpisode): DateTime | null =>
  parseISODate(episode.dischargeDate) ?? parseISODate(episode.closedDate);

const getAssessmentSentDate = (assessment: SurveyResponse) =>
  parseISODate(assessment.createdDate);

export const selectCurrentAssessmentEpisode = (
  episodes: TreatmentEpisode[] = [],
): TreatmentEpisode | null => {
  if (episodes.length === 0) return null;

  const sortedEpisodes = [...episodes].sort(
    (a, b) =>
      dateValue(b.admitDate ?? b.createdAt) -
      dateValue(a.admitDate ?? a.createdAt),
  );

  const activeEpisode = sortedEpisodes.find(
    (episode) =>
      episode.inTreatment ||
      ACTIVE_EPISODE_STATUS_CODES.has(
        episode.status?.code as PatientUniversalStatusCode,
      ),
  );

  return activeEpisode ?? sortedEpisodes[0];
};

export const isAssessmentInTreatmentEpisode = (
  assessment: SurveyResponse,
  episode?: TreatmentEpisode | null,
): boolean => {
  const episodeStart = episode ? getEpisodeStart(episode) : null;
  const assessmentSentDate = getAssessmentSentDate(assessment);

  if (!episodeStart || !assessmentSentDate) return false;

  const episodeEnd = getEpisodeEnd(episode);
  const sentAt = assessmentSentDate.startOf('day');

  if (sentAt.toMillis() < episodeStart.startOf('day').toMillis()) return false;

  return episodeEnd
    ? sentAt.toMillis() <= episodeEnd.endOf('day').toMillis()
    : true;
};

export const filterGroupedAssessmentToCurrentEpisode = (
  groupedAssessment: GroupedAssessment,
  episode?: TreatmentEpisode | null,
): GroupedAssessment => {
  if (!episode) return groupedAssessment;

  return {
    ...groupedAssessment,
    assessments: groupedAssessment.assessments.filter((assessment) =>
      isAssessmentInTreatmentEpisode(assessment, episode),
    ),
  };
};

const getWeek16Anchor = (episode: TreatmentEpisode): DateTime | null => {
  const admitDate = parseISODate(episode.admitDate);

  if (!admitDate) return null;

  return admitDate.plus({ weeks: 16 }).startOf('week');
};

export const buildAssessmentResponseHighlightZones = (
  groupedAssessment: GroupedAssessment,
): AssessmentChartHighlightZone[] =>
  groupedAssessment.assessments
    .filter((assessment) =>
      ACTIVE_ASSESSMENT_STATUSES.has(assessment.status as SurveyResponseStatus),
    )
    .map((assessment) => {
      const sentDate = getAssessmentSentDate(assessment);

      if (!sentDate) return null;

      const isOverdue =
        DateTime.now().diff(sentDate, 'days').days >= OVERDUE_ASSESSMENT_DAYS;
      const start = sentDate.startOf('day');
      const end = sentDate
        .plus({ days: ASSESSMENT_RESPONSE_WINDOW_DAYS })
        .endOf('day');

      const zone: AssessmentChartHighlightZone = {
        endEpoch: end.toMillis(),
        label: isOverdue ? 'Overdue' : 'Current assessment',
        layer: isOverdue ? 2 : 0,
        startEpoch: start.toMillis(),
        subLabel: formatHighlightDateRange(start, end),
        variant: isOverdue ? 'assessment-overdue' : 'assessment-current',
      };
      return zone;
    })
    .filter((zone): zone is AssessmentChartHighlightZone => zone !== null);

const getLatestNonVoidSentDate = (
  groupedAssessment: GroupedAssessment,
): DateTime | null => {
  const latest = groupedAssessment.assessments
    .filter((assessment) => assessment.status !== SurveyResponseStatus.VOID)
    .sort((a, b) => dateValue(b.createdDate) - dateValue(a.createdDate))[0];

  return latest ? parseISODate(latest.createdDate) : null;
};

const buildNextAssessmentHighlightZone = ({
  groupedAssessment,
  tentativeDischargeDate,
}: {
  groupedAssessment: GroupedAssessment;
  tentativeDischargeDate?: string | null;
}): AssessmentChartHighlightZone | null => {
  const latestSentDate = getLatestNonVoidSentDate(groupedAssessment);

  if (!latestSentDate) return null;

  const start = latestSentDate
    .plus({ days: ASSESSMENT_RESPONSE_WINDOW_DAYS })
    .startOf('day');
  const dischargeDate = parseISODate(tentativeDischargeDate);

  if (
    dischargeDate &&
    start.toMillis() > dischargeDate.startOf('day').toMillis()
  ) {
    return null;
  }

  const end = start
    .plus({ days: ASSESSMENT_RESPONSE_WINDOW_DAYS })
    .endOf('day');

  return {
    endEpoch: end.toMillis(),
    label: 'Next assessment',
    layer: 0,
    startEpoch: start.toMillis(),
    subLabel: formatHighlightDateRange(start, end),
    variant: 'assessment-next',
  };
};

export const buildAssessmentHighlightZones = (
  context: AssessmentTimepointContext & {
    groupedAssessment: GroupedAssessment;
  },
): AssessmentChartHighlightZone[] => {
  const nextAssessmentZone = buildNextAssessmentHighlightZone({
    groupedAssessment: context.groupedAssessment,
    tentativeDischargeDate: context.tentativeDischargeDate,
  });

  return [
    ...buildAssessmentResponseHighlightZones(context.groupedAssessment),
    ...(nextAssessmentZone ? [nextAssessmentZone] : []),
  ];
};

const isEpochInDomain = (epoch: number, domain?: [number, number]): boolean =>
  !domain || (epoch >= domain[0] && epoch <= domain[1]);

export const buildAssessmentTimelineMarkers = ({
  chartDomain,
  currentEpisode,
  tentativeDischargeDate,
}: AssessmentTimepointContext): AssessmentTimelineMarker[] => {
  if (!currentEpisode) return [];

  const admitDate = parseISODate(currentEpisode.admitDate);
  const week16Anchor = getWeek16Anchor(currentEpisode);
  const dischargeDate = parseISODate(tentativeDischargeDate);
  const markers: AssessmentTimelineMarker[] = [];

  if (admitDate) {
    const epoch = admitDate.startOf('day').toMillis();

    if (isEpochInDomain(epoch, chartDomain)) {
      markers.push({
        epoch,
        label: 'Admit',
        variant: 'admit',
      });
    }
  }

  if (week16Anchor) {
    const epoch = week16Anchor.startOf('day').toMillis();

    if (isEpochInDomain(epoch, chartDomain)) {
      markers.push({
        epoch,
        label: 'Week 16',
        variant: 'week16',
      });
    }
  }

  if (dischargeDate) {
    const epoch = dischargeDate.startOf('day').toMillis();

    if (isEpochInDomain(epoch, chartDomain)) {
      markers.push({
        epoch,
        label: 'Discharge',
        variant: 'discharge',
      });
    }
  }

  return markers;
};

export const getCurrentEpisodeChartDomain = (
  episode?: TreatmentEpisode | null,
  groupedAssessments: GroupedAssessment[] = [],
): [number, number] | undefined => {
  const admitDate = episode ? parseISODate(episode.admitDate) : null;

  if (!admitDate) return undefined;

  const episodeSentDates = groupedAssessments
    .flatMap((groupedAssessment) => groupedAssessment.assessments)
    .filter((assessment) => assessment.status !== SurveyResponseStatus.VOID)
    .filter((assessment) => isAssessmentInTreatmentEpisode(assessment, episode))
    .map((assessment) => getAssessmentSentDate(assessment))
    .filter((date): date is DateTime => !!date)
    .sort((a, b) => a.toMillis() - b.toMillis());

  const earliestSentAt = episodeSentDates[0];
  const latestSentAt = episodeSentDates[episodeSentDates.length - 1];

  if (!latestSentAt) return undefined;

  const admitMin = admitDate.minus({ months: 1 }).startOf('day');
  const earliestMin = earliestSentAt?.startOf('day');
  const domainMin =
    earliestMin && earliestMin.toMillis() < admitMin.toMillis()
      ? earliestMin
      : admitMin;

  const domainMax = latestSentAt.plus({ months: 2 }).endOf('day');

  return [domainMin.toMillis(), domainMax.toMillis()];
};
